<script setup lang="ts">
import { computed, ref } from "vue";
import { UiAside, UiButton, UiDivider, UiIcon, UiLinkButton, UiMultiSelectCombobox, UiSelect, UiText, type UiSelectOption } from "@shopexperts/shared";

import type { PartnerDirectoryRecordSort, PartnerDirectoryRecordStatusFilter } from "@/app/services/partnerDirectoryApi.service";
import {
  PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT,
  PARTNER_DIRECTORY_DEFAULT_MIN_RATING,
  PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS,
  PARTNER_DIRECTORY_DEFAULT_RECORD_SORT,
  PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER,
} from "@/app/stores";

// Presentation only: every control writes straight through to the page's
// existing v-models, so filters keep applying live while the drawer is open.
const props = defineProps<{
  statusOptions: UiSelectOption[];
  countryOptions: UiSelectOption[];
  minRatingOptions: UiSelectOption[];
  minReviewsOptions: UiSelectOption[];
  sortOptions: UiSelectOption[];
  pageSizeOptions: UiSelectOption[];
}>();

const emit = defineEmits<{
  (e: "reset"): void;
  (e: "select-all-countries"): void;
  (e: "deselect-all-countries"): void;
}>();

const status = defineModel<PartnerDirectoryRecordStatusFilter>("status", { required: true });
const countries = defineModel<string[]>("countries", { required: true });
const minRating = defineModel<string>("minRating", { required: true });
const minReviews = defineModel<string>("minReviews", { required: true });
const sort = defineModel<PartnerDirectoryRecordSort>("sort", { required: true });
const pageSize = defineModel<string>("pageSize", { required: true });

const isOpen = ref(false);

const selectedCountryCount = computed(() => {
  const selected = new Set(countries.value);
  return props.countryOptions.filter((option) => selected.has(option.value)).length;
});

// "All countries selected" is the default, so a partial selection counts as one active filter.
const hasCountryFilter = computed(() => props.countryOptions.length > 0 && selectedCountryCount.value < props.countryOptions.length);

const activeFilterCount = computed(() => {
  return [
    status.value !== PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER,
    hasCountryFilter.value,
    minRating.value !== PARTNER_DIRECTORY_DEFAULT_MIN_RATING,
    minReviews.value !== PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS,
    sort.value !== PARTNER_DIRECTORY_DEFAULT_RECORD_SORT,
    pageSize.value !== PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT,
  ].filter(Boolean).length;
});

const activeFilterSummary = computed(() => {
  if (activeFilterCount.value === 0) return "Showing the default view";
  return `${activeFilterCount.value} ${activeFilterCount.value === 1 ? "filter" : "filters"} differ from the default`;
});
</script>

<template>
  <UiButton variant="outline" class="w-full" label="Filters" @click="isOpen = true">
    <template #before_label>
      <UiIcon name="filter" class="h-4 w-4 text-se-gray-700" />
    </template>
    <template v-if="activeFilterCount > 0" #after_label>
      <span class="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-se-primary px-1 text-xs font-semibold text-se-dark">
        {{ activeFilterCount }}
      </span>
    </template>
  </UiButton>

  <UiAside v-model="isOpen" position="right">
    <div class="flex h-full flex-col">
      <div class="flex shrink-0 items-start justify-between gap-4 border-b border-se-gray-200 px-6 py-5">
        <div>
          <UiText as="h2" variant="h5" class="text-se-gray-950" text="Filter experts" />
          <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-600" :text="activeFilterSummary" />
        </div>
        <button type="button" class="-mr-1.5 cursor-pointer rounded-md p-1.5 transition-colors hover:bg-se-gray-100" aria-label="Close filters" @click="isOpen = false">
          <UiIcon name="close" class="h-5 w-5 text-se-gray-600" />
        </button>
      </div>

      <div class="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-6">
        <section>
          <UiText as="label" variant="caption" class="mb-2 block text-se-gray-600" text="Screening status" />
          <UiSelect v-model="status" name="indexed-expert-status-filter" :options="props.statusOptions" />
          <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-500" text="“To screen” hides experts you already rejected." />
        </section>

        <UiDivider color="light" />

        <section>
          <div class="mb-2 flex items-baseline justify-between gap-2">
            <UiText as="label" variant="caption" class="block text-se-gray-600" text="Countries" />
            <UiText as="span" variant="caption-normal" class="text-se-gray-500" :text="`${selectedCountryCount}/${props.countryOptions.length}`" />
          </div>
          <UiMultiSelectCombobox
            v-model="countries"
            name="indexed-expert-country-filter"
            :options="props.countryOptions"
            placeholder="Filter by country"
            openOnClick
            showChevron
          />
          <div class="mt-2 flex items-center gap-3">
            <UiLinkButton label="Select all" variant="ghost" text-variant="caption-normal" hover-decoration="underline" @click="emit('select-all-countries')" />
            <UiLinkButton label="Deselect all" variant="ghost" text-variant="caption-normal" hover-decoration="underline" @click="emit('deselect-all-countries')" />
          </div>
          <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-500" text="Countries found by new crawls are selected automatically." />
        </section>

        <UiDivider color="light" />

        <section>
          <UiText as="label" variant="caption" class="mb-2 block text-se-gray-600" text="Minimum social proof" />
          <div class="grid gap-3 sm:grid-cols-2">
            <div>
              <UiText as="span" variant="caption-normal" class="mb-1 block text-se-gray-500" text="Rating" />
              <UiSelect v-model="minRating" name="indexed-expert-min-rating" :options="props.minRatingOptions" />
            </div>
            <div>
              <UiText as="span" variant="caption-normal" class="mb-1 block text-se-gray-500" text="Reviews" />
              <UiSelect v-model="minReviews" name="indexed-expert-min-reviews" :options="props.minReviewsOptions" />
            </div>
          </div>
        </section>

        <UiDivider color="light" />

        <section>
          <UiText as="label" variant="caption" class="mb-2 block text-se-gray-600" text="List order" />
          <UiSelect v-model="sort" name="indexed-expert-sort" :options="props.sortOptions" />
        </section>

        <section>
          <UiText as="label" variant="caption" class="mb-2 block text-se-gray-600" text="Experts per page" />
          <div class="sm:w-40">
            <UiSelect v-model="pageSize" name="indexed-expert-row-limit" :options="props.pageSizeOptions" />
          </div>
          <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-500" text="Larger pages make bulk selection faster but load slower." />
        </section>
      </div>

      <div class="flex shrink-0 items-center justify-between gap-3 border-t border-se-gray-200 px-6 py-4">
        <UiButton variant="outline" label="Reset filters" :disabled="activeFilterCount === 0" @click="emit('reset')" />
        <UiButton variant="primary" label="Done" @click="isOpen = false" />
      </div>
    </div>
  </UiAside>
</template>
